import {compact} from '@benchling/js-utils';
import {Draft} from 'immer';
import {Connection} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {
  findAllDownstreamMappableNodeIds,
  findAllUpstreamMappableNodeIds,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';

import {isNewEdgeDuplicate} from '../edgeUtils';
import {getFieldInfoFromDataFlowEdgeHandle, makeDataFlowEdgeHandle} from '../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartEdge,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TransitionSource,
} from '../processFlowchartConfigEditorConstants';
import {StaticFlowchartNodeData, isStaticTaskSchemaNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartEdgeChange} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {
  getTransitionSourceByNodeIdPairKey,
  getNodeIdPairFromTransitionSourceKey,
} from '../TransitionSourceUtils';

/**
 * Finds nodes that have the same PTDV ID as the node with the given ID.
 */
export function findNodesWithMatchingPTDV(
  nodes: Array<StaticFlowchartNodeData>,
  nodeId: string
): Array<string> {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return [];
  }
  if (isStaticTaskSchemaNodeData(node)) {
    const taskDefinitionVersionId = node.taskDefinitionVersionSpec.clientId;
    const nodeDetailsType = node.nodeDetailsType;
    return nodes
      .filter(
        (n) =>
          isStaticTaskSchemaNodeData(n) &&
          n.taskDefinitionVersionSpec.clientId === taskDefinitionVersionId &&
          n.nodeDetailsType === nodeDetailsType
      )
      .map((n) => n.id);
  }
  return [];
}

/**
 * Gets edges of type @edgeType that are connected by the same PTDV IDs as the given source and target node IDs.
 */
export function extractMatchingEdges(
  edges: Array<FlowchartStaticEdgeData>,
  sourceNodeId: string,
  targetNodeId: string,
  nodes: Array<StaticFlowchartNodeData>,
  edgeType: ProcessFlowchartEdgeType,
  excludeOriginalPair: boolean = true
): Array<FlowchartStaticEdgeData> {
  const sourceNodeIds = findNodesWithMatchingPTDV(nodes, sourceNodeId);
  const targetNodeIds = findNodesWithMatchingPTDV(nodes, targetNodeId);

  const matchingEdges = edges.filter((edge) => {
    const matchesType = edge.type === edgeType;
    const matchesSource = sourceNodeIds.includes(edge.source);
    const matchesTarget = targetNodeIds.includes(edge.target);
    const isOriginalPair = edge.source === sourceNodeId && edge.target === targetNodeId;

    return matchesType && matchesSource && matchesTarget && (!excludeOriginalPair || !isOriginalPair);
  });

  return matchingEdges;
}

/**
 * Gets node pairs with PTDV IDs that match the given source and target node IDs and are connected by control flow edges
 * (these control flow edges may or may not go through router nodes)
 */
export function extractMatchingNodePairs(
  edges: Array<FlowchartStaticEdgeData>,
  sourceNodeId: string,
  targetNodeId: string,
  nodes: Array<StaticFlowchartNodeData>,
  excludeOriginalPair: boolean = true
): Array<{sourceNodeId: string; targetNodeId: string}> {
  const matchingSourceNodeIds = findNodesWithMatchingPTDV(nodes, sourceNodeId);
  const matchingTargetNodeIds = findNodesWithMatchingPTDV(nodes, targetNodeId);
  const nodePairs: Array<{sourceNodeId: string; targetNodeId: string}> = [];
  const nodesWithData = nodes.map((node) => ({id: node.id, data: node}));
  for (const matchingSourceNodeId of matchingSourceNodeIds) {
    const downstreamMappableNodeIds = findAllDownstreamMappableNodeIds(
      {nodes: nodesWithData, edges},
      matchingSourceNodeId
    );
    for (const downstreamMappableNodeId of downstreamMappableNodeIds) {
      const isOriginalPair =
        matchingSourceNodeId === sourceNodeId && downstreamMappableNodeId === targetNodeId;
      if (
        matchingTargetNodeIds.includes(downstreamMappableNodeId) &&
        (!excludeOriginalPair || !isOriginalPair)
      ) {
        nodePairs.push({sourceNodeId: matchingSourceNodeId, targetNodeId: downstreamMappableNodeId});
      }
    }
  }
  return nodePairs;
}

/**
 * Helper function to copy custom mapping data from a source edge to mirrored edges.
 */
export function copyCustomMappingDataToMirroredEdges(
  sourceEdgeId: string,
  mirroredEdgeIds: Array<string>,
  customMappingDataByEdgeId?: Record<string, CustomMappingData>,
  onChangeCustomMappingData?: (edgeId: string, newCustomMappingData: CustomMappingData) => void
): void {
  if (!customMappingDataByEdgeId || !onChangeCustomMappingData) {
    return;
  }

  const sourceCustomMappingData = customMappingDataByEdgeId[sourceEdgeId];
  if (sourceCustomMappingData) {
    // Copy the custom mapping data to all mirrored edges
    for (const mirroredEdgeId of mirroredEdgeIds) {
      onChangeCustomMappingData(mirroredEdgeId, sourceCustomMappingData);
    }
  }
}

/**
 * Finds data flow edges between nodes that have the same PTDV IDs as @edge and are connected by control flow edges
 */
export function findMirroredEdges(
  edge: FlowchartStaticEdgeData,
  sourceNodeId: string,
  targetNodeId: string,
  sourceHandle: string,
  targetHandle: string,
  edges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>
): Array<FlowchartStaticEdgeData> {
  const nodePairsMatchingDataFlowEdge = extractMatchingNodePairs(edges, sourceNodeId, targetNodeId, nodes);

  const sourceInfo = getFieldInfoFromDataFlowEdgeHandle(sourceHandle);
  const targetInfo = getFieldInfoFromDataFlowEdgeHandle(targetHandle);

  return edges.filter((edge) => {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
      return false;
    }
    const edgeSourceInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
    const edgeTargetInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
    const handlesMatch =
      edgeSourceInfo.fieldClientId === sourceInfo.fieldClientId &&
      edgeSourceInfo.sectionType === sourceInfo.sectionType &&
      edgeTargetInfo.fieldClientId === targetInfo.fieldClientId &&
      edgeTargetInfo.sectionType === targetInfo.sectionType;
    const hasMatchingControlFlow = nodePairsMatchingDataFlowEdge.some(
      (nodePair) => nodePair.sourceNodeId === edge.source && nodePair.targetNodeId === edge.target
    );
    return handlesMatch && hasMatchingControlFlow;
  });
}

/**
 * Processes edge changes with mirroring logic, performs mirroring for mapping and transition source data.
 */
export function processEdgeChangesWithMirroring(
  edgeChanges: Array<FlowchartEdgeChange>,
  draft: Draft<FlowchartConfigWithMetadata>,
  options?: {
    customMappingDataByEdgeId?: Record<string, CustomMappingData>;
    onChangeCustomMappingData?: (edgeId: string, newCustomMappingData: CustomMappingData) => void;
    transitionSourceByNodeIdPair?: Record<string, TransitionSource>;
    onChangeTransitionSource?: (key: string, newTransitionSource: TransitionSource) => void;
  }
): Array<FlowchartEdgeChange> {
  const allEdgeChanges = [...edgeChanges];
  const {
    customMappingDataByEdgeId,
    onChangeCustomMappingData,
    transitionSourceByNodeIdPair,
    onChangeTransitionSource,
  } = options || {};

  for (const edgeChange of edgeChanges) {
    if (edgeChange.type === 'remove') {
      // Mirroring changes for removed data flow edges
      const deletedEdge = draft.staticFlowchart.edgesStaticData.find((edge) => edge.id === edgeChange.id);
      if (deletedEdge && deletedEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        const edgesBetweenSamePTDVs = extractMatchingEdges(
          draft.staticFlowchart.edgesStaticData,
          deletedEdge.source,
          deletedEdge.target,
          draft.staticFlowchart.nodesStaticData,
          ProcessFlowchartEdgeType.DATA_FLOW
        );
        // Edges between nodes that are connected by control flow edges, potentially through router nodes
        const edgesBetweenSamePTDVsWithControlFlow = edgesBetweenSamePTDVs.filter((edge) =>
          findAllDownstreamMappableNodeIds(
            {
              nodes: draft.staticFlowchart.nodesStaticData.map((node) => ({id: node.id, data: node})),
              edges: draft.staticFlowchart.edgesStaticData,
            },
            edge.source
          ).includes(edge.target)
        );

        if (edgesBetweenSamePTDVsWithControlFlow.length > 0) {
          const deletedSourceInfo = getFieldInfoFromDataFlowEdgeHandle(deletedEdge.sourceHandle);
          const deletedTargetInfo = getFieldInfoFromDataFlowEdgeHandle(deletedEdge.targetHandle);
          const mirrorEdgesToRemove = edgesBetweenSamePTDVsWithControlFlow
            .filter((edge) => {
              const edgeSourceInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
              const edgeTargetInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
              return (
                edgeSourceInfo.fieldClientId === deletedSourceInfo.fieldClientId &&
                edgeSourceInfo.sectionType === deletedSourceInfo.sectionType &&
                edgeTargetInfo.fieldClientId === deletedTargetInfo.fieldClientId &&
                edgeTargetInfo.sectionType === deletedTargetInfo.sectionType
              );
            })
            .map((edge) => edge.id);

          allEdgeChanges.push(
            ...mirrorEdgesToRemove.map((edgeId) => ({type: 'remove' as const, id: edgeId}))
          );
        }
      }
    } else if (edgeChange.type === 'add') {
      // Mirroring changes for added data flow edges
      const newEdge = edgeChange.item;
      if (newEdge && newEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        const nodePairsToMirrorDataFlow = extractMatchingNodePairs(
          draft.staticFlowchart.edgesStaticData,
          newEdge.source,
          newEdge.target,
          draft.staticFlowchart.nodesStaticData
        );

        if (nodePairsToMirrorDataFlow.length > 0) {
          const mirrorEdges = compact(
            nodePairsToMirrorDataFlow.map((nodePair) => {
              const sourceInfo = getFieldInfoFromDataFlowEdgeHandle(newEdge.sourceHandle!);
              const targetInfo = getFieldInfoFromDataFlowEdgeHandle(newEdge.targetHandle!);
              const mirrorEdge: FlowchartEdge = {
                source: nodePair.sourceNodeId,
                target: nodePair.targetNodeId,
                id: uuidv4(),
                sourceHandle: makeDataFlowEdgeHandle({
                  nodeId: nodePair.sourceNodeId,
                  fieldClientId: sourceInfo.fieldClientId,
                  sectionType: sourceInfo.sectionType,
                  handleType: 'source',
                }),
                targetHandle: makeDataFlowEdgeHandle({
                  nodeId: nodePair.targetNodeId,
                  fieldClientId: targetInfo.fieldClientId,
                  sectionType: targetInfo.sectionType,
                  handleType: 'target',
                }),
                type: ProcessFlowchartEdgeType.DATA_FLOW,
              };
              // Filter out duplicate edges
              if (isNewEdgeDuplicate(draft.staticFlowchart.edgesStaticData, mirrorEdge)) {
                return null;
              }
              return mirrorEdge;
            })
          );

          allEdgeChanges.push(...mirrorEdges.map((mirrorEdge) => ({type: 'add' as const, item: mirrorEdge})));

          // Copy custom mapping onto new data flow edges data if available
          if (customMappingDataByEdgeId && onChangeCustomMappingData) {
            const newEdgeSourceInfo = getFieldInfoFromDataFlowEdgeHandle(newEdge.sourceHandle!);
            const newEdgeTargetInfo = getFieldInfoFromDataFlowEdgeHandle(newEdge.targetHandle!);
            const existingEdgeWithSameHandles = draft.staticFlowchart.edgesStaticData.find((edge) => {
              if (
                edge.type !== ProcessFlowchartEdgeType.DATA_FLOW ||
                customMappingDataByEdgeId[edge.id] == null
              ) {
                return false;
              }
              const edgeSourceInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
              const edgeTargetInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
              const handlesMatch =
                edgeSourceInfo.fieldClientId === newEdgeSourceInfo.fieldClientId &&
                edgeSourceInfo.sectionType === newEdgeSourceInfo.sectionType &&
                edgeTargetInfo.fieldClientId === newEdgeTargetInfo.fieldClientId &&
                edgeTargetInfo.sectionType === newEdgeTargetInfo.sectionType;
              const nodesMatch =
                findNodesWithMatchingPTDV(draft.staticFlowchart.nodesStaticData, edge.source).includes(
                  newEdge.source
                ) &&
                findNodesWithMatchingPTDV(draft.staticFlowchart.nodesStaticData, edge.target).includes(
                  newEdge.target
                );
              return handlesMatch && nodesMatch;
            });

            if (existingEdgeWithSameHandles) {
              copyCustomMappingDataToMirroredEdges(
                existingEdgeWithSameHandles.id,
                [...mirrorEdges.map((edge) => edge.id), newEdge.id],
                customMappingDataByEdgeId,
                onChangeCustomMappingData
              );
            }
          }
        }
      } else if (newEdge && newEdge.type === ProcessFlowchartEdgeType.CONTROL_FLOW) {
        // Mirroring data flow edges for new control flow edges
        const existingDataFlowEdgesBetweenNodes = draft.staticFlowchart.edgesStaticData.filter(
          (edge) =>
            edge.source === newEdge.source &&
            edge.target === newEdge.target &&
            edge.type === ProcessFlowchartEdgeType.DATA_FLOW
        );

        // Add data flow edges that are on other nodes with matching PTDVs
        const dataFlowEdgesToMirrorControlFlow = extractMatchingEdges(
          draft.staticFlowchart.edgesStaticData,
          newEdge.source,
          newEdge.target,
          draft.staticFlowchart.nodesStaticData,
          ProcessFlowchartEdgeType.DATA_FLOW
        );

        const sourceHandleAndTargetPairsToMirror = new Map<
          string,
          {sourceHandle: string; targetHandle: string}
        >();
        for (const dataFlowEdge of dataFlowEdgesToMirrorControlFlow) {
          const key = `${dataFlowEdge.sourceHandle}:${dataFlowEdge.targetHandle}`;
          sourceHandleAndTargetPairsToMirror.set(key, {
            sourceHandle: dataFlowEdge.sourceHandle,
            targetHandle: dataFlowEdge.targetHandle,
          });
        }

        const mirrorEdgesToAdd = compact(
          Array.from(sourceHandleAndTargetPairsToMirror.values()).map(({sourceHandle, targetHandle}) => {
            const sourceInfo = getFieldInfoFromDataFlowEdgeHandle(sourceHandle);
            const targetInfo = getFieldInfoFromDataFlowEdgeHandle(targetHandle);
            return {
              id: uuidv4(),
              type: ProcessFlowchartEdgeType.DATA_FLOW,
              source: newEdge.source,
              target: newEdge.target,
              sourceHandle: makeDataFlowEdgeHandle({
                nodeId: newEdge.source,
                fieldClientId: sourceInfo.fieldClientId,
                sectionType: sourceInfo.sectionType,
                handleType: 'source',
              }),
              targetHandle: makeDataFlowEdgeHandle({
                nodeId: newEdge.target,
                fieldClientId: targetInfo.fieldClientId,
                sectionType: targetInfo.sectionType,
                handleType: 'target',
              }),
            } as FlowchartEdge;
          })
        );

        if (mirrorEdgesToAdd.length > 0) {
          allEdgeChanges.push(...mirrorEdgesToAdd.map((edge) => ({type: 'add' as const, item: edge})));
        }

        // Copy transition source information for new data flow edges if available
        if (transitionSourceByNodeIdPair && onChangeTransitionSource) {
          const nodePairsToMirror = extractMatchingNodePairs(
            draft.staticFlowchart.edgesStaticData,
            newEdge.source,
            newEdge.target,
            draft.staticFlowchart.nodesStaticData
          );
          const nodesWithData = draft.staticFlowchart.nodesStaticData.map((node) => ({
            id: node.id,
            data: node,
          }));
          if (nodePairsToMirror.length > 0) {
            const targetNodeId = nodePairsToMirror[0].targetNodeId;
            const sourceNodeId = nodePairsToMirror[0].sourceNodeId;
            // Find the first node upstream of targetNode on the path from sourceNode -> targetNode
            const upstreamNodeId = draft.staticFlowchart.edgesStaticData.find(
              (edge) =>
                edge.target === targetNodeId &&
                (edge.source === sourceNodeId ||
                  findAllUpstreamMappableNodeIds(
                    {nodes: nodesWithData, edges: draft.staticFlowchart.edgesStaticData},
                    edge.source
                  ).includes(sourceNodeId))
            )?.source;
            if (!upstreamNodeId) {
              continue;
            }
            const sourceKey = getTransitionSourceByNodeIdPairKey(upstreamNodeId, targetNodeId);
            const existingTransitionSource = transitionSourceByNodeIdPair[sourceKey];
            if (existingTransitionSource) {
              const newKey = getTransitionSourceByNodeIdPairKey(newEdge.source, newEdge.target);
              onChangeTransitionSource(newKey, existingTransitionSource);
            }
          }
        }

        // Copy custom mapping data for mirrored edges
        if (customMappingDataByEdgeId && onChangeCustomMappingData) {
          for (const mirroredEdge of mirrorEdgesToAdd) {
            const mirroredEdgeSourceInfo = getFieldInfoFromDataFlowEdgeHandle(mirroredEdge.sourceHandle);
            const mirroredEdgeTargetInfo = getFieldInfoFromDataFlowEdgeHandle(mirroredEdge.targetHandle);
            const mirroredEdges = dataFlowEdgesToMirrorControlFlow.filter((edge: FlowchartStaticEdgeData) => {
              const edgeSourceInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
              const edgeTargetInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
              return (
                edgeSourceInfo.fieldClientId === mirroredEdgeSourceInfo.fieldClientId &&
                edgeSourceInfo.sectionType === mirroredEdgeSourceInfo.sectionType &&
                edgeTargetInfo.fieldClientId === mirroredEdgeTargetInfo.fieldClientId &&
                edgeTargetInfo.sectionType === mirroredEdgeTargetInfo.sectionType
              );
            });
            const mirroredEdgeWithCustomMappingData = mirroredEdges.find(
              (edge: FlowchartStaticEdgeData) => customMappingDataByEdgeId[edge.id] != null
            );
            if (mirroredEdgeWithCustomMappingData) {
              copyCustomMappingDataToMirroredEdges(
                mirroredEdgeWithCustomMappingData.id,
                [mirroredEdge.id],
                customMappingDataByEdgeId,
                onChangeCustomMappingData
              );
            }
          }
        }

        // Mirror existing data flow edges for the new control flow edge
        for (const dataFlowEdge of existingDataFlowEdgesBetweenNodes) {
          const additionalMirrorEdges = processDataFlowEdgeMirroring(
            {
              source: newEdge.source,
              target: newEdge.target,
              sourceHandle: dataFlowEdge.sourceHandle,
              targetHandle: dataFlowEdge.targetHandle,
            },
            draft.staticFlowchart.edgesStaticData,
            draft.staticFlowchart.nodesStaticData,
            customMappingDataByEdgeId,
            onChangeCustomMappingData
          );
          allEdgeChanges.push(...additionalMirrorEdges.map((edge) => ({type: 'add' as const, item: edge})));
        }
      }
    }
  }

  return allEdgeChanges;
}

/**
 * Helper function to process data flow edge mirroring
 */
function processDataFlowEdgeMirroring(
  connection: Connection,
  edges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  customMappingDataByEdgeId?: Record<string, CustomMappingData>,
  onChangeCustomMappingData?: (edgeId: string, newCustomMappingData: CustomMappingData) => void
): Array<FlowchartEdge> {
  const nodePairsToMirror = extractMatchingNodePairs(edges, connection.source!, connection.target!, nodes);

  if (nodePairsToMirror.length === 0) {
    return [];
  }

  const mirrorEdges = compact(
    nodePairsToMirror.map((nodePair) => {
      assert(connection.sourceHandle != null, 'Source handle must be defined');
      assert(connection.targetHandle != null, 'Target handle must be defined');
      const sourceInfo = getFieldInfoFromDataFlowEdgeHandle(connection.sourceHandle);
      const targetInfo = getFieldInfoFromDataFlowEdgeHandle(connection.targetHandle);
      const newEdge = {
        source: nodePair.sourceNodeId,
        target: nodePair.targetNodeId,
        id: uuidv4(),
        sourceHandle: makeDataFlowEdgeHandle({
          nodeId: nodePair.sourceNodeId,
          fieldClientId: sourceInfo.fieldClientId,
          sectionType: sourceInfo.sectionType,
          handleType: 'source',
        }),
        targetHandle: makeDataFlowEdgeHandle({
          nodeId: nodePair.targetNodeId,
          fieldClientId: targetInfo.fieldClientId,
          sectionType: targetInfo.sectionType,
          handleType: 'target',
        }),
        type: ProcessFlowchartEdgeType.DATA_FLOW,
      };
      // Filter out duplicate edges
      if (isNewEdgeDuplicate(edges, newEdge)) {
        return null;
      }
      return newEdge;
    })
  );

  // Copy custom mapping data if available
  if (customMappingDataByEdgeId && onChangeCustomMappingData && mirrorEdges.length > 0) {
    const connectionSourceInfo = getFieldInfoFromDataFlowEdgeHandle(connection.sourceHandle!);
    const connectionTargetInfo = getFieldInfoFromDataFlowEdgeHandle(connection.targetHandle!);
    const existingEdgeWithSameHandles = edges.find((edge) => {
      if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW || customMappingDataByEdgeId[edge.id] == null) {
        return false;
      }
      const edgeSourceInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
      const edgeTargetInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
      const handlesMatch =
        edgeSourceInfo.fieldClientId === connectionSourceInfo.fieldClientId &&
        edgeSourceInfo.sectionType === connectionSourceInfo.sectionType &&
        edgeTargetInfo.fieldClientId === connectionTargetInfo.fieldClientId &&
        edgeTargetInfo.sectionType === connectionTargetInfo.sectionType;
      const nodesMatch =
        findNodesWithMatchingPTDV(nodes, edge.source).includes(connection.source!) &&
        findNodesWithMatchingPTDV(nodes, edge.target).includes(connection.target!);
      return handlesMatch && nodesMatch;
    });

    if (existingEdgeWithSameHandles) {
      copyCustomMappingDataToMirroredEdges(
        existingEdgeWithSameHandles.id,
        mirrorEdges.map((edge) => edge.id),
        customMappingDataByEdgeId,
        onChangeCustomMappingData
      );
    }
  }

  return mirrorEdges;
}

/**
 * Mirrors transition source changes or removals to corresponding control flow edges
 */
export function mirrorTransitionSource(
  nodeIdPairKey: string,
  newTransitionSource: TransitionSource | null,
  edges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  onChangeTransitionSource: (key: string, transitionSource: TransitionSource) => void,
  onRemoveTransitionSource: (key: string) => void
): void {
  const {sourceNodeId, targetNodeId} = getNodeIdPairFromTransitionSourceKey(nodeIdPairKey);

  const correspondingNodePairs = extractMatchingNodePairs(edges, sourceNodeId, targetNodeId, nodes);

  for (const nodePair of correspondingNodePairs) {
    const mirrorKey = getTransitionSourceByNodeIdPairKey(nodePair.sourceNodeId, nodePair.targetNodeId);
    if (newTransitionSource != null) {
      onChangeTransitionSource(mirrorKey, newTransitionSource);
    } else {
      onRemoveTransitionSource(mirrorKey);
    }
  }
}

/**
 * Mirrors custom mapping data changes or removals to corresponding data flow edges
 */
export function mirrorCustomMappingData(
  edgeId: string,
  newCustomMappingData: CustomMappingData | null,
  edges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  onChangeCustomMappingData: (edgeId: string, customMappingData: CustomMappingData) => void,
  onRemoveCustomMappingData: (edgeId: string) => void
): void {
  const edge = edges.find((e) => e.id === edgeId);
  if (!edge || edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return;
  }
  const correspondingDataFlowEdges = findMirroredEdges(
    edge,
    edge.source,
    edge.target,
    edge.sourceHandle,
    edge.targetHandle,
    edges,
    nodes
  );

  for (const mirrorEdge of correspondingDataFlowEdges) {
    if (newCustomMappingData != null) {
      onChangeCustomMappingData(mirrorEdge.id, newCustomMappingData);
    } else {
      onRemoveCustomMappingData(mirrorEdge.id);
    }
  }
}
